import { collection, addDoc } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import headerImage from "../assets/img3.png";
import { db } from "../firebase";
import "../styles/ReviewForm.css";

const ratingQuestions = [
  { key: "staff", label: "How were the staff?" },
  { key: "facilities", label: "How were the facilities?" },
  { key: "cleanliness", label: "How clean was it?" },
  { key: "food", label: "How was the food?" },
  { key: "value", label: "Was it good value for money?" },
];

const emojiOptions = [
  { value: 1, symbol: "☹", label: "Poor" },
  { value: 2, symbol: "😐", label: "Okay" },
  { value: 3, symbol: "🙂", label: "Good" },
  { value: 4, symbol: "😁", label: "Excellent" },
];

const getRatingLabel = (value) => {
  const matchedOption = emojiOptions.find((option) => option.value === value);
  return matchedOption ? `${value} - ${matchedOption.label}` : "Not rated";
};

const ReviewForm = () => {
  const [ratings, setRatings] = useState({
    staff: 0,
    facilities: 0,
    cleanliness: 0,
    food: 0,
    value: 0,
  });
  const [review, setReview] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [ipAddress, setIpAddress] = useState("Unknown");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitButtonRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const hotelName = queryParams.get("hotel") || "Centauria Hill Resort";

  // Brevo configuration
  const BREVO_API_KEY = process.env.REACT_APP_BREVO_API_KEY;
  const BREVO_SENDER_EMAIL = process.env.REACT_APP_BREVO_SENDER_EMAIL;
  const BREVO_SENDER_NAME = process.env.REACT_APP_BREVO_SENDER_NAME;
  const BREVO_TO_EMAILS = process.env.REACT_APP_BREVO_TO_EMAILS
    ? process.env.REACT_APP_BREVO_TO_EMAILS.split(",").map((email) =>
        email.trim()
      )
    : [];

  // Validate API key on component mount
  useEffect(() => {
    if (!BREVO_API_KEY) {
      console.error("BREVO_API_KEY is not defined. Check your .env file.");
    } else {
      console.log("Brevo API Key loaded successfully");
    }
  }, [BREVO_API_KEY]);

  const handleRating = (type, value) => {
    setRatings((prev) => ({ ...prev, [type]: value }));
  };

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const isFormValid =
    Object.values(ratings).every((val) => val > 0) &&
    (!email || isValidEmail(email));

  const sendEmailViaBrevo = async (templateParams) => {
    try {
      const recipientEmails = BREVO_TO_EMAILS.map((email) => ({ email }));

      const emailContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            @keyframes slideIn {
              from { opacity: 0; transform: translateY(20px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.8; }
            }
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              line-height: 1.6;
              color: #333;
            }
            .container {
              max-width: 600px;
              margin: 0 auto;
              background: #f8f9fa;
              animation: fadeIn 0.6s ease-in;
            }
            .header {
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              padding: 40px 20px;
              text-align: center;
              animation: slideIn 0.8s ease-out;
            }
            .header h1 {
              margin: 0;
              font-size: 28px;
              animation: pulse 2s infinite;
            }
            .content {
              padding: 30px 20px;
              animation: slideIn 1s ease-out;
            }
            .info-card {
              background: white;
              border-radius: 8px;
              padding: 20px;
              margin-bottom: 15px;
              border-left: 4px solid #667eea;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              transition: all 0.3s ease;
              animation: slideIn 0.8s ease-out backwards;
            }
            .info-card:nth-child(1) { animation-delay: 0.1s; }
            .info-card:nth-child(2) { animation-delay: 0.2s; }
            .info-card:nth-child(3) { animation-delay: 0.3s; }
            .info-card:nth-child(4) { animation-delay: 0.4s; }
            .info-card:nth-child(5) { animation-delay: 0.5s; }
            .info-card:nth-child(6) { animation-delay: 0.6s; }
            .info-card:hover {
              border-left-color: #764ba2;
              box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
            }
            .label {
              font-weight: 600;
              color: #667eea;
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            .value {
              font-size: 16px;
              color: #333;
              margin-top: 5px;
            }
            .ratings-section {
              background: linear-gradient(135deg, #667eea15 0%, #764ba215 100%);
              border-radius: 8px;
              padding: 20px;
              margin-bottom: 15px;
              animation: slideIn 1.2s ease-out;
            }
            .rating-item {
              display: inline-block;
              margin-right: 15px;
              margin-bottom: 10px;
              animation: slideIn 1.3s ease-out backwards;
            }
            .rating-item:nth-child(1) { animation-delay: 0.7s; }
            .rating-item:nth-child(2) { animation-delay: 0.8s; }
            .rating-item:nth-child(3) { animation-delay: 0.9s; }
            .rating-item:nth-child(4) { animation-delay: 1s; }
            .rating-item:nth-child(5) { animation-delay: 1.1s; }
            .rating-label {
              font-size: 12px;
              color: #667eea;
              font-weight: 600;
            }
            .rating-value {
              font-size: 14px;
              color: #333;
              margin-top: 3px;
            }
            .review-section {
              background: white;
              border-radius: 8px;
              padding: 20px;
              margin-bottom: 15px;
              border-left: 4px solid #764ba2;
              animation: slideIn 1.4s ease-out;
            }
            .review-section .label {
              color: #764ba2;
            }
            .footer {
              text-align: center;
              padding: 20px;
              font-size: 12px;
              color: #999;
              animation: fadeIn 1.5s ease-in;
            }
            .divider {
              height: 2px;
              background: linear-gradient(90deg, transparent, #667eea, transparent);
              margin: 20px 0;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>✨ New Hotel Review</h1>
              <p style="margin: 10px 0 0 0; opacity: 0.95;">Thank you for the feedback!</p>
            </div>

            <div class="content">
              <div class="info-card">
                <div class="label">🏨 Hotel Name</div>
                <div class="value">${templateParams.hotel_name}</div>
              </div>

              <div class="info-card">
                <div class="label">👤 Guest Name</div>
                <div class="value">${templateParams.name}</div>
              </div>

              <div class="info-card">
                <div class="label">📧 Guest Email</div>
                <div class="value">${templateParams.email}</div>
              </div>

              <div class="info-card">
                <div class="label">⭐ Overall Rating</div>
                <div class="value" style="font-size: 20px; color: #667eea;">${templateParams.overall_label}</div>
              </div>

              <div class="divider"></div>

              <div class="ratings-section">
                <div style="font-weight: 600; color: #667eea; margin-bottom: 15px;">📊 Detailed Ratings</div>
                
                <div class="rating-item">
                  <div class="rating-label">👨‍💼 Staff</div>
                  <div class="rating-value">${templateParams.staff_label}</div>
                </div>

                <div class="rating-item">
                  <div class="rating-label">🏢 Facilities</div>
                  <div class="rating-value">${templateParams.facilities_label}</div>
                </div>

                <div class="rating-item">
                  <div class="rating-label">✨ Cleanliness</div>
                  <div class="rating-value">${templateParams.cleanliness_label}</div>
                </div>

                <div class="rating-item">
                  <div class="rating-label">🍽️ Food</div>
                  <div class="rating-value">${templateParams.food_label}</div>
                </div>

                <div class="rating-item">
                  <div class="rating-label">💰 Value</div>
                  <div class="rating-value">${templateParams.value_label}</div>
                </div>
              </div>

              <div class="review-section">
                <div class="label">💬 Guest Review</div>
                <div class="value" style="margin-top: 10px; font-style: italic; color: #666;">
                  "${templateParams.review}"
                </div>
              </div>

              <div class="info-card">
                <div class="label">🌐 IP Address</div>
                <div class="value">${templateParams.ip_address}</div>
              </div>

              <div class="info-card">
                <div class="label">⏰ Submitted At</div>
                <div class="value">${templateParams.submitted_at}</div>
              </div>
            </div>

            <div class="footer">
              <p>This is an automated message from Hotel Review System. Please do not reply to this email.</p>
              <p style="margin-top: 10px;">© ${new Date().getFullYear()} Hotel Review App. All rights reserved.</p>
            </div>
          </div>
        </body>
        </html>
      `;

      const emailPayload = {
        sender: {
          name: BREVO_SENDER_NAME,
          email: BREVO_SENDER_EMAIL,
        },
        to: recipientEmails,
        subject: `✨ New Review for ${templateParams.hotel_name}`,
        htmlContent: emailContent,
      };

      // Add replyTo only if guest email is provided
      if (templateParams.email !== "None") {
        emailPayload.replyTo = {
          email: templateParams.email,
          name: templateParams.name,
        };
      }

      console.log("Sending email via Brevo with payload:", emailPayload);

      const response = await fetch("https://api.brevo.com/v3/smtp/email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "api-key": BREVO_API_KEY,
        },
        body: JSON.stringify(emailPayload),
      });

      const responseData = await response.json();
      console.log("Brevo API response:", responseData);

      if (!response.ok) {
        throw new Error(
          `Brevo API error (${response.status}): ${responseData.message || response.statusText}`
        );
      }

      return true;
    } catch (error) {
      console.error("Error sending email via Brevo:", error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting || !isFormValid) {
      return;
    }

    setIsSubmitting(true);

    const ratingValues = Object.values(ratings);
    const overallScore = (
      ratingValues.reduce((sum, value) => sum + value, 0) / ratingValues.length
    ).toFixed(1);

    const templateParams = {
      name: name || "None",
      email: email || "None",
      hotel_name: hotelName,
      overall: overallScore,
      overall_label: `${overallScore}/4`,
      staff: ratings.staff,
      staff_label: getRatingLabel(ratings.staff),
      facilities: ratings.facilities,
      facilities_label: getRatingLabel(ratings.facilities),
      cleanliness: ratings.cleanliness,
      cleanliness_label: getRatingLabel(ratings.cleanliness),
      food: ratings.food,
      food_label: getRatingLabel(ratings.food),
      value: ratings.value,
      value_label: getRatingLabel(ratings.value),
      review: review || "None",
      ip_address: ipAddress,
      submitted_at: new Date().toLocaleString(),
    };

    try {
      await addDoc(collection(db, "hotelReviews"), {
        ...ratings,
        review,
        name,
        email,
        createdAt: new Date(),
        hotelName,
        ipAddress,
      });

      await sendEmailViaBrevo(templateParams);

      navigate("/thank-you");

      setRatings({
        staff: 0,
        facilities: 0,
        cleanliness: 0,
        food: 0,
        value: 0,
      });
      setReview("");
      setName("");
      setEmail("");
    } catch (error) {
      console.error("Error submitting review or sending email:", error);
      alert("Submission failed or email not sent. Please try again.");
      setIsSubmitting(false);
    }
  };

  const renderEmojiSelector = (type) => (
    <div className="emoji-scale" role="radiogroup" aria-label={type}>
      {emojiOptions.map((option) => {
        const isSelected = ratings[type] === option.value;

        return (
          <button
            key={option.value}
            type="button"
            className={isSelected ? "emoji-option selected" : "emoji-option"}
            onClick={() => handleRating(type, option.value)}
            aria-pressed={isSelected}
            aria-label={option.label}
          >
            <span className="emoji-symbol" aria-hidden="true">
              {option.symbol}
            </span>
          </button>
        );
      })}
    </div>
  );

  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) => setIpAddress(data.ip))
      .catch((err) => console.error("IP fetch failed", err));
  }, []);

  useEffect(() => {
    if (isFormValid && submitButtonRef.current) {
      submitButtonRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      submitButtonRef.current.focus();
    }
  }, [isFormValid]);

  return (
    <div className="review-wrapper">
      <div className="review-overlay"></div>

      <div className="review-container">
        <div className="review-header">
          <img className="review-hero-image" src={headerImage} alt={hotelName} />
          <div className="review-hero-overlay"></div>
          <div className="review-hero-content">
            <p className="review-eyebrow">Review</p>
            <h3 className="review-title">{hotelName}</h3>
            <p className="review-subtitle">Share your stay experience with us</p>
          </div>
        </div>

        <div className="meal-options">
          <h4>Tell us what we can improve</h4>
          <hr />

          <div className="ratings-grid">
            {ratingQuestions.map(({ key, label }) => (
              <div key={key} className="rating-row">
                <label>{label}</label>
                <div>{renderEmojiSelector(key)}</div>
              </div>
            ))}
          </div>

          <label className="field-label" htmlFor="review-message">
            Suggestions (optional)
          </label>
          <textarea
            id="review-message"
            placeholder="Share your experience..."
            value={review}
            onChange={(e) => setReview(e.target.value)}
          />
          <label className="field-label" htmlFor="review-name">
            Name (optional)
          </label>
          <input
            id="review-name"
            type="text"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <label className="field-label" htmlFor="review-email">
            Email (optional)
          </label>
          <input
            id="review-email"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          {email && !isValidEmail(email) && (
            <p className="validation-error">
              Please enter a valid email address.
            </p>
          )}
        </div>

        <div className="review-footer">
          <div className="review-score-chip">
            <span>Ready to submit</span>
            <strong>
              {Object.values(ratings).filter((value) => value > 0).length}/5
            </strong>
          </div>

          {isFormValid && (
            <button
              ref={submitButtonRef}
              className="submit-btn"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : " Submit Feedback"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewForm;
